/**
 * formatHours.js — worked time rendered as HOURS:MINUTES ("7:30").
 *
 * ADDED in 1.17.0.
 *
 * WHO SEES WHAT
 *   Employees and customers read a working day, not a payroll line: a shift that
 *   ended at half past three is "7:30", never "7.5". Every read-only hours value
 *   under pages/employee, pages/customer and components/employee goes through
 *   these helpers.
 *   Admin surfaces deliberately keep DECIMAL hours — payroll (the Danløn CSV
 *   quantities), invoicing (e-conomic line quantities) and the admin reports are
 *   all built on decimal hours — so nothing under pages/admin or
 *   components/admin imports this module.
 *
 * TWO ENTRY POINTS, because the API speaks both dialects:
 *   formatMinutes(450)      -> "7:30"   whole minutes. duration_minutes is the
 *                                       canonical column, so prefer this
 *                                       wherever the payload carries a
 *                                       *Minutes field.
 *   formatHoursMinutes(7.5) -> "7:30"   decimal hours. For the aggregate
 *                                       endpoints (customer employees/reports,
 *                                       employee day + dashboard rows) that only
 *                                       publish `hours`.
 *   Entries serialized by the backend's serializeTimeEntry already carry a ready
 *   made `durationFormatted` string — use that first and keep these as the
 *   fallback.
 *
 * RELATED, AND DELIBERATELY SEPARATE
 *   utils/time.js exports its own formatMinutes next to parseTimeToMinutes.
 *   That pair is the round-trip partner for the editable weekly grid: it must
 *   read back exactly what the user may type, so "nothing" is 0:00 there. These
 *   helpers are display-only, so "nothing" is a dash — a day with no data must
 *   not masquerade as a day with zero hours.
 */

/** Rendered when a value is absent or unusable. Pass '' where the UI wants blank. */
const MISSING = '—';

/**
 * Format whole minutes as "H:MM".
 *
 *   formatMinutes(450)   -> "7:30"
 *   formatMinutes(45)    -> "0:45"
 *   formatMinutes(0)     -> "0:00"
 *   formatMinutes(null)  -> "—"      (or `fallback`)
 *
 * Rounds to the nearest minute BEFORE splitting, so 59.6 becomes 1:00 and the
 * output can never read "7:60". Negative input should never occur, but it
 * degrades to a signed "-0:30" rather than to garbage.
 *
 * @param {number|string|null|undefined} minutes whole (or fractional) minutes
 * @param {string} [fallback] what to render for null/undefined/''/NaN
 * @returns {string} "H:MM"
 */
export const formatMinutes = (minutes, fallback = MISSING) => {
  if (minutes === null || minutes === undefined || minutes === '') return fallback;
  const value = Number(minutes);
  if (!Number.isFinite(value)) return fallback;

  const total = Math.round(value);
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(total);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};

/**
 * Format decimal hours as "H:MM".
 *
 *   formatHoursMinutes(7.5)   -> "7:30"
 *   formatHoursMinutes(0.75)  -> "0:45"
 *   formatHoursMinutes(0)     -> "0:00"
 *   formatHoursMinutes(null)  -> "—"      (or `fallback`)
 *
 * Same rounding and sign rules as formatMinutes — 7.999 hours is 8:00, never
 * 7:60.
 *
 * @param {number|string|null|undefined} hours decimal hours
 * @param {string} [fallback] what to render for null/undefined/''/NaN
 * @returns {string} "H:MM"
 */
export const formatHoursMinutes = (hours, fallback = MISSING) => {
  if (hours === null || hours === undefined || hours === '') return fallback;
  const value = Number(hours);
  if (!Number.isFinite(value)) return fallback;
  return formatMinutes(value * 60, fallback);
};
